import React, { useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import {
  collection, query, where, onSnapshot, doc, getDoc,
} from 'firebase/firestore';
import { Headphones, Clock, CheckCircle2, PlayCircle, LogOut } from 'lucide-react';
import { isNative, isOnline } from '../lib/offlineStorage';
import { listRentalDownloads } from '../lib/offlineRentalStore';

/* ═══════════════════════════════════════════
   LISTENING LIBRARY
   The reader's own audiobooks — rentals and purchases — shown after a
   passwordless sign-in. Tapping a (non-expired) book opens the existing
   AudiobookPlayer via the /listen/{id} route; the player is reused, never
   reimplemented here.

   Uses the .listening-theme palette (near-black, antique gold, crimson,
   Cormorant Garamond). Mobile-first.
═══════════════════════════════════════════ */

// Firestore Timestamps expose toMillis(); accept a few other shapes defensively.
function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  return 0;
}

// Render the time remaining on a rental in plain British English, e.g.
// "5 months left", "11 days left", "1 day left". Returns '' when nothing is left
// (the caller shows "Expired" instead).
function formatTimeLeft(expiresAtMs, nowMs) {
  const ms = expiresAtMs - nowMs;
  if (ms <= 0) return '';
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.ceil(ms / dayMs);
  if (days <= 1) return '1 day left';
  if (days < 31) return `${days} days left`;
  const months = Math.round(days / 30.44);
  return months <= 1 ? '1 month left' : `${months} months left`;
}

// Map a stored offline-rental manifest to the same entitlement shape the cards use.
function offlineRecordToEntitlement(rec) {
  const book = rec.book || {};
  const expiresAtMs = rec.expiresAt || 0;
  return {
    key: `offline_${rec.audiobookId}`, kind: 'rental', audiobookId: rec.audiobookId,
    authorId: book.authorId || null, title: book.title || null,
    authorName: book.authorName || null, coverUrl: book.coverUrl || null,
    expiresAtMs, expired: expiresAtMs ? expiresAtMs <= Date.now() : false, offline: true,
  };
}

export default function ListeningLibrary({ user }) {
  const uid = user?.uid;

  const [rentals, setRentals] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [meta, setMeta] = useState({});        // audiobookId -> richer metadata
  const [started, setStarted] = useState({});  // audiobookId -> bool (progress exists)
  const [loading, setLoading] = useState(true);
  // 'unknown' until we know connectivity; gates whether we touch Firestore at all.
  const [netStatus, setNetStatus] = useState('unknown'); // 'unknown' | 'online' | 'offline'
  const [offlineEntitlements, setOfflineEntitlements] = useState([]);

  // Snapshot "now" once at mount so the displayed time-left and expiry checks are
  // stable across re-renders. Good enough for a library view; the player itself
  // re-verifies expiry on playback.
  const nowMs = useMemo(() => Date.now(), []);

  // Determine connectivity once. Web is always 'online'; native asks the Network plugin.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNative()) { if (!cancelled) setNetStatus('online'); return; }
      let online = true;
      try { online = await isOnline(); } catch { online = true; }
      if (!cancelled) setNetStatus(online ? 'online' : 'offline');
    })();
    return () => { cancelled = true; };
  }, []);

  // OFFLINE: build the library purely from local download manifests — ZERO Firestore.
  useEffect(() => {
    if (netStatus !== 'offline' || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const recs = await listRentalDownloads();
        const mine = recs
          .filter(r => r && r.book && (r.uid ? r.uid === uid : true))
          .map(offlineRecordToEntitlement)
          .filter(e => e.audiobookId);
        if (!cancelled) { setOfflineEntitlements(mine); setLoading(false); }
      } catch {
        if (!cancelled) { setOfflineEntitlements([]); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [netStatus, uid]);

  // ─── Subscribe to this reader's entitlements (real time) ───
  // Modelled on ReaderLibrary's audiobookPurchases subscription, with the rentals
  // subscription added in the same style.
  useEffect(() => {
    if (!uid || netStatus !== 'online') return;
    const unsubs = [];

    const purchasesQ = query(collection(db, 'audiobookPurchases'), where('uid', '==', uid));
    unsubs.push(onSnapshot(purchasesQ, snap => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setPurchases(items);
    }, err => console.error('audiobookPurchases listener:', err)));

    const rentalsQ = query(collection(db, 'audiobookRentals'), where('uid', '==', uid));
    unsubs.push(onSnapshot(rentalsQ, snap => {
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      setRentals(items);
    }, err => console.error('audiobookRentals listener:', err)));

    // Both listeners are set up; clear the loading state after a tick.
    const timer = setTimeout(() => setLoading(false), 600);

    return () => {
      unsubs.forEach(u => u());
      clearTimeout(timer);
    };
  }, [uid, netStatus]);

  // ─── Merge rentals + purchases into one entitlement per audiobook ───
  // A permanent purchase always wins over a rental for the same audiobook
  // (owned, no expiry), so a reader who later buys a book they rented sees one
  // "owned" card rather than two.
  const entitlements = useMemo(() => {
    const byId = new Map();

    for (const r of rentals) {
      if (!r.audiobookId) continue;
      const expiresAtMs = toMillis(r.expiresAt);
      const expired = r.status !== 'active' || expiresAtMs <= nowMs;
      byId.set(r.audiobookId, {
        key: r.id,
        kind: 'rental',
        audiobookId: r.audiobookId,
        authorId: r.authorId,
        title: r.title,
        authorName: r.authorName,
        expiresAtMs,
        expired,
      });
    }

    for (const p of purchases) {
      if (!p.audiobookId) continue;
      byId.set(p.audiobookId, {
        key: p.id,
        kind: 'purchase',
        audiobookId: p.audiobookId,
        authorId: p.authorId,
        title: p.title,
        authorName: p.authorName,
        expired: false,
      });
    }

    return Array.from(byId.values());
  }, [rentals, purchases, nowMs]);

  // Stable dependency for the metadata/progress effects below — they only need to
  // re-run when the set of (author, audiobook) pairs changes, not on every render.
  const metaKey = useMemo(
    () => entitlements.map(e => `${e.authorId}:${e.audiobookId}`).join(','),
    [entitlements],
  );

  // ─── Fetch richer metadata (cover, chapterCount) per audiobook ───
  // Same fallback pattern as ReaderLibrary: if the audiobook doc cannot be read
  // (rules, deletion, draft status), fall back to the title/authorName stored on
  // the entitlement doc so the card still renders.
  useEffect(() => {
    if (netStatus !== 'online' || entitlements.length === 0) { setMeta({}); return; }
    let cancelled = false;

    (async () => {
      const next = {};
      for (const e of entitlements) {
        const { authorId, audiobookId, title, authorName } = e;
        if (!authorId || !audiobookId) continue;
        try {
          const snap = await getDoc(doc(db, 'authors', authorId, 'audiobooks', audiobookId));
          if (snap.exists()) {
            next[audiobookId] = { id: snap.id, authorId, ...snap.data() };
          } else {
            next[audiobookId] = { id: audiobookId, authorId, title, authorName };
          }
        } catch {
          // Rules may block the read — use the entitlement's own fields.
          next[audiobookId] = { id: audiobookId, authorId, title, authorName };
        }
      }
      if (!cancelled) setMeta(next);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaKey, netStatus]);

  // ─── Track whether each book has been started ───
  // audiobookProgress/{uid}_{audiobookId} exists once the reader has begun. We
  // only need existence (the player stores currentChapterId/positionSeconds, not
  // a percentage), so we surface a simple "Started" / "Not started".
  useEffect(() => {
    if (netStatus !== 'online' || !uid || entitlements.length === 0) { setStarted({}); return; }
    const unsubs = [];

    for (const e of entitlements) {
      const progressDocId = `${uid}_${e.audiobookId}`;
      unsubs.push(onSnapshot(
        doc(db, 'audiobookProgress', progressDocId),
        snap => setStarted(prev => ({ ...prev, [e.audiobookId]: snap.exists() })),
        () => {}, // missing/blocked progress simply means "not started"
      ));
    }

    return () => unsubs.forEach(u => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, metaKey, netStatus]);

  function openBook(e) {
    if (e.expired) return; // expired rentals do not open the player
    window.location.href = `/listen/${e.audiobookId}`;
  }

  // Sign out — the auth-state change returns the reader to the sign-in screen
  // (the ListeningApp shell swaps SignInScreen back in).
  async function handleSignOut() {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  }

  // ─── Loading ───
  if (loading) {
    return (
      <div className="listening-theme min-h-screen w-full bg-background text-foreground flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      </div>
    );
  }

  return (
    <div className="listening-theme min-h-screen w-full bg-background text-foreground px-5 py-8">
      <div className="relative mx-auto w-full max-w-3xl">
        {/* Sign out — unobtrusive, top corner */}
        <button
          type="button"
          onClick={handleSignOut}
          title="Sign out"
          className="text-muted-foreground hover:text-primary absolute right-0 top-0 inline-flex items-center gap-1.5 text-xs transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>

        {/* Wordmark */}
        <header className="mb-8 text-center">
          <h1 className="font-display text-primary text-4xl font-semibold tracking-wide">
            NARRATORIA
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Your library</p>
        </header>

        {(netStatus === 'offline' ? offlineEntitlements : entitlements).length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {(netStatus === 'offline' ? offlineEntitlements : entitlements).map(e => (
              <LibraryCard
                key={e.key}
                entitlement={e}
                meta={meta[e.audiobookId] || {}}
                started={!!started[e.audiobookId]}
                nowMs={nowMs}
                onOpen={() => openBook(e)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   LIBRARY CARD
───────────────────────────────────────── */

function LibraryCard({ entitlement, meta, started, nowMs, onOpen }) {
  const e = entitlement;
  const title = meta.title || e.title || 'Untitled';
  const authorName = meta.authorName || e.authorName || '';
  const coverUrl = meta.coverUrl || e.coverUrl || '';

  const isRental = e.kind === 'rental';
  const timeLeft = isRental && !e.expired ? formatTimeLeft(e.expiresAtMs, nowMs) : '';

  const clickable = !e.expired;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={clickable ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onOpen(); } } : undefined}
      className={[
        'group flex flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground',
        clickable ? 'cursor-pointer transition-shadow hover:shadow-lg hover:shadow-black/40' : 'opacity-70',
      ].join(' ')}
    >
      {/* Cover */}
      <div className="relative aspect-square overflow-hidden rounded-t-lg bg-secondary">
        {coverUrl ? (
          <img src={coverUrl} alt={title} className="h-full w-full object-cover"
               onError={(ev) => { ev.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Headphones className="h-9 w-9 text-muted-foreground" />
          </div>
        )}

        {/* Status badge */}
        <div className="absolute left-1.5 top-1.5">
          {e.expired ? (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-destructive-foreground"
              style={{ backgroundColor: 'hsl(var(--crimson))' }}
            >
              Expired
            </span>
          ) : isRental ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-primary">
              <Clock className="h-3 w-3" /> {timeLeft}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-background/70 px-2 py-0.5 text-[10px] font-semibold text-primary">
              <CheckCircle2 className="h-3 w-3" /> Owned
            </span>
          )}
        </div>

        {/* Play affordance on hover for non-expired books */}
        {clickable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity group-hover:bg-black/30 group-hover:opacity-100">
            <PlayCircle className="h-12 w-12 text-primary" />
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className="font-display line-clamp-2 text-base font-semibold leading-tight">
          {title}
        </h3>
        {authorName && (
          <p className="text-muted-foreground text-xs">{authorName}</p>
        )}
        <p className="text-muted-foreground mt-auto pt-1 text-[11px]">
          {e.expired ? 'Rental ended' : started ? 'Started' : 'Not started'}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   EMPTY STATE
───────────────────────────────────────── */

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16 text-center">
      <div className="bg-secondary mb-4 flex h-16 w-16 items-center justify-center rounded-full">
        <Headphones className="text-muted-foreground h-8 w-8" />
      </div>
      <h2 className="font-display text-foreground text-xl font-semibold">Your library is empty</h2>
      <p className="text-muted-foreground mt-2 max-w-xs text-sm">
        Audiobooks you buy or rent will appear here, ready to listen wherever you are.
      </p>
    </div>
  );
}
